import { ArticleClass, Topic, TopicArticle } from '@lemon8/webapp-seo-shared';
import { Link, useParams } from '@remix-run/react';
import { useCallback, FC, useMemo, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '~/components/image';
import { useNumFormatter } from '~/utils/format/num-formatter';
import VideoIcon from 'icon/play_fill.svg';
import HeartIcon from 'icon/heart.svg';
import { useAtomValue } from 'jotai';
import { $Locale, $UserAgent } from '@lemon8/webapp-shared/model';
import { ClickPosition } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';

import './style.scss';

interface TopicArticleCardProps {
  topic: Topic;
  article: TopicArticle;
}

export const TopicArticleCard: FC<TopicArticleCardProps> = ({ topic, article }) => {
  const { topicId } = topic;
  const { canonicalUri, articleClass, articleLikes, shortContent, title, largeImage, groupId } = article;
  const { region } = useAtomValue($Locale);
  const { t } = useTranslation();
  const formatNum = useNumFormatter();
  const active = usePageActive();

  const articleHref = useMemo(() => `${canonicalUri}?region=${region}`, [region]);

  const handleArticleClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.related_hashtag,
        page_click_id: topicId,
      }),
      reflowBehavior: () => {
        active({
          schemaOption: {
            pageType: articleClass === ArticleClass.VIDEO ? 'video' : 'article',
            groupId,
          },
          ampExtra: {
            enter_position: ClickPosition.related_hashtag,
          },
        });
      },
      defaultBehavior: (event?: SyntheticEvent) => {
        event?.stopPropagation();
      },
    },
    [active, topicId, groupId],
  );

  return (
    <object>
      <Link
        className="hashtag-article-card"
        to={articleHref}
        onClick={handleArticleClick}
        draggable={false}
        target="_blank"
      >
        <div className="hashtag-article-card-cover">
          <div className="hashtag-article-card-cover-image">
            <Image src={largeImage.url} alt={t('own_picture').replace('%s', article.title || shortContent)} />
          </div>
          <div className="hashtag-article-card-cover-bg" />
          <div className="hashtag-article-card-cover-likes-count">
            <HeartIcon />
            <span>{formatNum(articleLikes)}</span>
          </div>
          {articleClass === ArticleClass.VIDEO && (
            <div className="hashtag-article-card-cover-video-badge">
              <VideoIcon />
            </div>
          )}
        </div>
        <div className="hashtag-article-card-detail">
          <p className="hashtag-article-card-detail-title">{title}</p>
          <p className="hashtag-article-card-detail-desc">{shortContent}</p>
        </div>
      </Link>
    </object>
  );
};
